package ui

import io.kvision.core.Color
import io.kvision.core.CssSize
import io.kvision.core.Display
import io.kvision.core.UNIT
import io.kvision.core.onClick
import io.kvision.html.Button
import io.kvision.html.button
import io.kvision.html.h4
import io.kvision.html.p
import io.kvision.html.span
import io.kvision.panel.SimplePanel
import io.kvision.panel.vPanel
import io.kvision.utils.perc
import io.kvision.utils.px
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger

/**
 * Map picker widget for Simulation Mode. Renders a vertical list of map cards
 * filtered by slot count. The partition mirrors the server-side map selection
 * logic at server/src/main/kotlin/gameInit/GameInit.kt:122-130 — keeping the
 * two in sync is required because a map the UI offers but the server rejects
 * (or vice versa) is a user-facing dead-end.
 *
 * Filter rules:
 *  - slotCount ≤ 2: 2-player maps (San_Martello, Arctica)
 *  - slotCount ≥ 3: 3-4 player maps (IO-map, Laurasiagondwana, jupiter, Europa)
 *  - StartMap.map: always shown regardless of slot count
 *
 * UI coherence 2026-08-09: cards use the `.simulation-map-card` class with
 * hover + active states matching the commander cards on Step 1.
 *
 * @param onMapSelected fired when the user clicks a map card. The selection
 *                      is also reflected in the UI by toggling the
 *                      `simulation-map-card-active` class.
 */
class SimulationMapPicker(
    private val slotCount: Int,
    private val onMapSelected: (String) -> Unit
) : SimplePanel(className = "simulation-map-picker")
{
    private var selectedPath: String = ""
    private val mapCardEntries = mutableListOf<MapCardEntry>()

    init
    {
        width = 100.perc
        vPanel(spacing = 8) {
            width = 100.perc

            h4("Map") {
                textAlign = io.kvision.core.TextAlign.CENTER
                fontSize = CssSize(20, UNIT.px)
                color = Color("#f4f6fb")
                marginBottom = 4.px
            }

            renderMaps(this)
        }
    }

    private fun renderMaps(parent: io.kvision.panel.VPanel)
    {
        availableMaps().forEach { mapPath ->
            val card = SimplePanel().apply {
                addCssClass("simulation-map-card")
                padding = CssSize(14, UNIT.px)
                borderRadius = 8.px
                border = io.kvision.core.Border(
                    1.px,
                    io.kvision.core.BorderStyle.SOLID,
                    Color("rgba(255, 255, 255, 0.08)")
                )
                cursor = io.kvision.core.Cursor.POINTER

                vPanel(spacing = 4) {
                    span(mapDisplayName(mapPath)) {
                        fontSize = CssSize(18, UNIT.px)
                        fontWeight = io.kvision.core.FontWeight.BOLD
                        color = Color("#ffffff")
                    }
                    span(mapSubtitle(mapPath)) {
                        fontSize = CssSize(12, UNIT.px)
                        color = Color("#cbd3ff")
                    }
                }

                onClick {
                    selectMap(mapPath, this)
                }
            }
            mapCardEntries.add(MapCardEntry(mapPath, card))
            parent.add(card)
        }
    }

    private data class MapCardEntry(
        val mapPath: String,
        val card: SimplePanel
    )

    private fun mapSubtitle(mapPath: String): String = when
    {
        mapPath == "maps/StartMap.map" -> "Tutorial map — always available"
        mapPath in listOf("maps/San_Martello.map", "maps/Arctica.map") -> "2-player map"
        mapPath in listOf(
            "maps/IO-map.map",
            "maps/Laurasiagondwana.map",
            "maps/jupiter.map",
            "maps/Europa.map"
        ) -> "3-4 player map"
        else -> ""
    }

    /**
     * Visible inventory for the current slot count. Exposed via a thin
     * wrapper that callers (and tests) can read without rebuilding the
     * partition logic.
     */
    fun availableMaps(): List<String>
    {
        val twoPlayer = listOf("maps/San_Martello.map", "maps/Arctica.map")
        val threeFour = listOf(
            "maps/IO-map.map",
            "maps/Laurasiagondwana.map",
            "maps/jupiter.map",
            "maps/Europa.map"
        )
        val always = listOf("maps/StartMap.map")
        return if (slotCount <= 2) twoPlayer + always else threeFour + always
    }

    private fun mapDisplayName(mapPath: String): String = mapPath.removePrefix("maps/").removeSuffix(".map")

    private fun selectMap(mapPath: String, clickedCard: SimplePanel)
    {
        Logger.info(LogCategory.UI, "SimulationMapPicker: map selected '$mapPath'")
        mapCardEntries.forEach { entry ->
            if (entry.card == clickedCard)
            {
                entry.card.addCssClass("simulation-map-card-active")
            }
            else
            {
                entry.card.removeCssClass("simulation-map-card-active")
            }
        }
        selectedPath = mapPath
        onMapSelected(mapPath)
    }

    /** Programmatic selection — used by the page when state changes externally. */
    fun setSelectedProgrammatically(mapPath: String)
    {
        if (mapPath == selectedPath) return
        selectedPath = mapPath
        mapCardEntries.forEach { entry ->
            val isMatch = entry.mapPath == mapPath
            if (isMatch)
            {
                entry.card.addCssClass("simulation-map-card-active")
            }
            else
            {
                entry.card.removeCssClass("simulation-map-card-active")
            }
        }
    }

    fun selectedPathForTest(): String = selectedPath
}
